import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Tv, 
  ExternalLink, 
  Play,
  Radio,
  TrendingUp,
  Rss,
  Globe
} from 'lucide-react';

// Channel configurations - clickable cards that open live streams
const CHANNELS = [
  {
    id: 'cnbc',
    name: 'CNBC',
    nameAr: 'سي إن بي سي',
    youtubeUrl: 'https://www.youtube.com/@CNBCtelevision/streams',
    directUrl: 'https://www.cnbc.com/live-tv/',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/40',
    hoverBorder: 'hover:border-green-400',
    icon: TrendingUp,
    description: 'Business News',
    headline: 'Stock Market & Business Updates',
    bgImage: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&q=80'
  },
  {
    id: 'alarabiya',
    name: 'Al Arabiya',
    nameAr: 'العربية',
    youtubeUrl: 'https://www.youtube.com/AlArabiya/live',
    directUrl: 'https://www.alarabiya.net/live',
    color: 'from-rose-500 to-red-600',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/40',
    hoverBorder: 'hover:border-rose-400',
    icon: Rss,
    description: 'Arabic News',
    headline: 'أخبار الأسواق العربية',
    bgImage: 'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=400&q=80'
  }
];

// Channel Card Component
const ChannelCard = ({ channel }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = channel.icon;

  const openStream = (url, e) => {
    e?.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-xl border-2 ${channel.borderColor} ${channel.hoverBorder} ${channel.bgColor} group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/20`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => openStream(channel.youtubeUrl)}
      data-testid={`channel-card-${channel.id}`}
    >
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20 group-hover:opacity-30 transition-opacity duration-300"
        style={{ backgroundImage: `url(${channel.bgImage})` }}
      />
      
      {/* Gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${channel.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

      {/* Content */}
      <div className="relative p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <Badge className={`${channel.bgColor} ${channel.borderColor} border text-xs font-mono font-bold`}>
            {channel.name}
          </Badge>
          <Badge className="bg-red-600 text-white text-[10px] px-2 py-0.5 font-mono animate-pulse">
            <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 inline-block"></span>
            LIVE
          </Badge>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${channel.color} flex items-center justify-center shadow-lg mb-3 transform transition-all duration-300 ${isHovered ? 'scale-110 rotate-3' : ''}`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-lg font-bold text-white mb-0.5 font-['Manrope']">{channel.name}</h4>
          <p className="text-xs text-slate-400 mb-1">{channel.nameAr}</p>
          <p className="text-[10px] text-slate-500">{channel.description}</p>
        </div>

        {/* Play indicator - shows on hover */}
        <div className={`flex items-center justify-center gap-2 py-2 rounded-lg transition-all duration-300 ${isHovered ? 'bg-white/10' : 'bg-transparent'}`}>
          <Play className={`w-4 h-4 text-white fill-white transition-transform duration-300 ${isHovered ? 'scale-125' : ''}`} />
          <span className="text-sm text-white font-medium">Click to Watch</span>
        </div>

        {/* Headline */}
        <div className="text-center py-2 border-t border-white/10 mt-2">
          <p className="text-[10px] text-slate-300 truncate font-mono">{channel.headline}</p>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button
            onClick={(e) => openStream(channel.youtubeUrl, e)}
            className={`bg-gradient-to-r ${channel.color} hover:opacity-90 text-white text-xs font-bold gap-1 h-9 rounded-lg shadow-lg`}
            size="sm"
          >
            <Play className="w-3 h-3 fill-white" />
            YouTube
          </Button>
          <Button
            onClick={(e) => openStream(channel.directUrl, e)}
            variant="outline"
            className="border-white/20 hover:bg-white/10 text-white text-xs font-bold gap-1 h-9 rounded-lg"
            size="sm"
          >
            <ExternalLink className="w-3 h-3" />
            Direct
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function LiveStreamGrid() {
  return (
    <Card className="bg-[#0D131C] border-[#1E293B] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] bg-gradient-to-r from-[#060A10] to-[#0D131C]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-500/20 rounded-xl border border-red-500/30 shadow-lg shadow-red-500/10">
              <Tv className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2 font-['Manrope']">
                Live Financial News
                <Badge className="bg-red-600 text-white text-[10px] px-2 py-0.5 font-mono">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse inline-block"></span>
                  2 LIVE
                </Badge>
              </h3>
              <p className="text-xs text-slate-400">Click any channel to watch live stream</p>
            </div>
          </div>
          
          <Badge variant="outline" className="border-cyan-500/50 text-cyan-400 font-mono">
            <Radio className="w-3 h-3 mr-1" />
            24/7
          </Badge>
        </div>
      </div>

      {/* 4-Way Grid */}
      <div className="grid grid-cols-2 gap-3 p-4 bg-gradient-to-b from-[#060A10] to-[#0A0E14]">
        {CHANNELS.map((channel) => (
          <ChannelCard key={channel.id} channel={channel} />
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 bg-[#0D131C] border-t border-[#1E293B]">
        <p className="text-[10px] text-slate-500 text-center font-mono">
          Click any channel card to open live stream in a new tab
        </p>
      </div>
    </Card>
  );
}
